import React from 'react';
import { X, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';

const InstagramSetupModal = ({ isOpen, onClose, errorType, errorDetails }) => {
  if (!isOpen) return null;

  const getSetupSteps = () => {
    if (errorType === 'NO_FACEBOOK_PAGE') {
      return {
        title: '📄 Facebook Sayfası Oluşturun',
        description: 'Instagram Business hesabı bağlamak için bir Facebook Sayfası gereklidir.',
        steps: [
          {
            title: 'Facebook Sayfası Oluşturun',
            description: 'facebook.com/pages/create adresinden yeni bir sayfa oluşturun',
            link: 'https://www.facebook.com/pages/create',
            linkText: 'Sayfa Oluştur'
          },
          {
            title: 'Instagram Business Hesabına Geçin',
            description: 'Instagram uygulamasında Ayarlar > Hesap > Profesyonel hesaba geç',
            link: null
          },
          {
            title: 'Instagram\'ı Facebook Sayfanıza Bağlayın',
            description: 'Instagram Ayarlar > Hesap > Bağlı Hesaplar > Facebook',
            link: null
          },
          {
            title: 'Social Octo\'ta Tekrar Deneyin',
            description: 'Ayarlar > Instagram > Hesabı Bağla',
            link: null
          }
        ]
      };
    } else if (errorType === 'NO_INSTAGRAM_ON_PAGE') {
      return {
        title: '🔗 Instagram\'ı Facebook Sayfanıza Bağlayın',
        description: errorDetails?.message || 'Facebook sayfanız bulundu ancak Instagram\'a bağlı değil.',
        steps: [
          {
            title: 'Instagram Business Hesabına Geçin',
            description: 'Instagram uygulamasında Ayarlar > Hesap > Profesyonel hesaba geç',
            link: null
          },
          {
            title: 'Instagram\'ı Facebook Sayfanıza Bağlayın',
            description: 'Instagram Ayarlar > Hesap > Bağlı Hesaplar > Facebook',
            link: null
          },
          {
            title: 'Facebook Sayfanızı Seçin',
            description: errorDetails?.pages ?
              `Şu sayfalardan birini seçin: ${errorDetails.pages.map(p => p.name).join(', ')}` :
              'Sahip olduğunuz Facebook sayfasını seçin',
            link: null
          },
          {
            title: 'Social Octo\'ta Tekrar Deneyin',
            description: 'Ayarlar > Instagram > Hesabı Bağla',
            link: null
          }
        ]
      };
    } else {
      return {
        title: '⚠️ Bağlantı Hatası',
        description: errorDetails?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.',
        steps: [
          {
            title: 'Hata Detayı',
            description: errorDetails?.error || 'Bilinmeyen hata',
            link: null
          }
        ]
      };
    }
  };

  const setupInfo = getSetupSteps();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{setupInfo.title}</h2>
            <p className="text-sm text-gray-600 mt-1">{setupInfo.description}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Steps */}
        <div className="p-6">
          <div className="space-y-6">
            {setupInfo.steps.map((step, index) => (
              <div key={index} className="flex gap-4">
                {/* Step Number */}
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white flex items-center justify-center font-bold">
                    {index + 1}
                  </div>
                </div>

                {/* Step Content */}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {step.description}
                  </p>
                  {step.link && (
                    <a
                      href={step.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {step.linkText || 'Aç'}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pages Info if available */}
          {errorDetails?.pages && errorDetails.pages.length > 0 && (
            <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Bulunan Facebook Sayfalarınız:
              </h4>
              <div className="space-y-2">
                {errorDetails.pages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3">
                    <span className="font-medium text-gray-900">{page.name}</span>
                    {page.hasInstagram ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm font-semibold">
                        <CheckCircle className="w-4 h-4" />
                        Instagram Bağlı
                      </span>
                    ) : (
                      <span className="text-orange-600 text-sm font-semibold">
                        Instagram Bağlı Değil
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">💡 Önemli Notlar:</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Instagram hesabınız <strong>Business</strong> veya <strong>Creator</strong> hesabı olmalıdır</li>
              <li>• Kişisel Instagram hesapları desteklenmemektedir</li>
              <li>• Instagram Business hesabı mutlaka bir Facebook Sayfasına bağlı olmalıdır</li>
              <li>• Bağlantı işlemi birkaç dakika sürebilir</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
          >
            Kapat
          </button>
          {errorDetails?.setupUrl && (
            <a
              href={errorDetails.setupUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition flex items-center gap-2"
            >
              Kuruluma Başla
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstagramSetupModal;
